import type { ReactNode } from "react";
import * as RadixTabs from "@radix-ui/react-tabs";

import { cn } from "../lib/cn";

export interface TabItem {
  value: string;
  label: string;
  /** Drawn before the label, e.g. a page's image. */
  icon?: ReactNode;
  content: ReactNode;
}

export interface TabsProps {
  label: string;
  defaultValue: string;
  value?: string;
  onValueChange?: (value: string) => void;
  activationMode?: "automatic" | "manual";
  orientation?: "horizontal" | "vertical";
  items: readonly TabItem[];
  className?: string;
  listClassName?: string;
  panelClassName?: string;
}

export function Tabs({
  label,
  defaultValue,
  value,
  onValueChange,
  activationMode,
  orientation = "horizontal",
  items,
  className,
  listClassName,
  panelClassName,
}: TabsProps) {
  return (
    <RadixTabs.Root
      defaultValue={defaultValue}
      value={value}
      onValueChange={onValueChange}
      activationMode={activationMode}
      orientation={orientation}
      className={className}
    >
      <RadixTabs.List
        aria-label={label}
        className={cn(
          "mb-4 flex overflow-x-auto overflow-y-hidden border-b border-border px-1",
          listClassName,
        )}
      >
        {items.map((item) => (
          <RadixTabs.Trigger
            key={item.value}
            value={item.value}
            className={cn(
              "inline-grid shrink-0 place-items-center whitespace-nowrap border-b-2 border-transparent px-4 py-2.5 text-sm font-medium text-muted transition-colors",
              "hover:font-semibold focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-focus",
              "data-[state=active]:border-interaction-text data-[state=active]:text-primary",
            )}
          >
            <span className="col-start-1 row-start-1 inline-flex items-center gap-1.5">
              {item.icon}
              {item.label}
            </span>
            <span
              aria-hidden="true"
              className="invisible col-start-1 row-start-1 inline-flex items-center gap-1.5 font-semibold"
            >
              {item.icon}
              {item.label}
            </span>
          </RadixTabs.Trigger>
        ))}
      </RadixTabs.List>
      {items.map((item) => (
        <RadixTabs.Content
          key={item.value}
          value={item.value}
          className={cn("focus-visible:outline-none", panelClassName)}
        >
          {item.content}
        </RadixTabs.Content>
      ))}
    </RadixTabs.Root>
  );
}
